"use client";

import Link from "next/link";
import { useState } from "react";

import {
  Badge,
  Button,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import {
  ITEM_CATEGORY_VALUES,
  type ItemCategory,
  type ItemResponse,
  type ListItemsQuery,
} from "@stocks/shared";

import { usePaginatedList } from "@/shared/libs/query";
import {
  ITEM_CATEGORY_LABELS,
  ItemCategoryBadge,
  ItemThumbnail,
} from "@/entities/item";

const PAGE_SIZE = 20;

/** 품목 목록: 이름/코드/규격 검색 + 분류·사용 여부 필터 + 페이지네이션 */
export const ItemsTable = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<ItemCategory | "">("");
  const [status, setStatus] = useState<"" | "true" | "false">("");

  const query: ListItemsQuery = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    category: category || undefined,
    isActive: status === "" ? undefined : status === "true",
  };
  const { data, isLoading, isError } = usePaginatedList<
    ItemResponse,
    ListItemsQuery
  >("items", "/v1/items", query);

  const resetPage = () => setPage(1);

  return (
    <div className='flex flex-col gap-4'>
      <form
        className='flex flex-wrap gap-2'
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(keyword.trim());
          resetPage();
        }}
      >
        <Input
          className='w-64'
          placeholder='이름 / 코드 / 규격 검색'
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button type='submit'>검색</Button>
        <NativeSelect
          aria-label='카테고리 필터'
          value={category}
          onChange={(e) => {
            setCategory(e.target.value as ItemCategory | "");
            resetPage();
          }}
        >
          <option value=''>전체 분류</option>
          {ITEM_CATEGORY_VALUES.map((c) => (
            <option key={c} value={c}>
              {ITEM_CATEGORY_LABELS[c]}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label='사용 여부 필터'
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as "" | "true" | "false");
            resetPage();
          }}
        >
          <option value=''>전체 상태</option>
          <option value='true'>사용 중</option>
          <option value='false'>사용 중지</option>
        </NativeSelect>
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-16'>사진</TableHead>
            <TableHead>코드</TableHead>
            <TableHead>품목명</TableHead>
            <TableHead>분류</TableHead>
            <TableHead>규격</TableHead>
            <TableHead>단위</TableHead>
            <TableHead>상태</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={7} className='text-center text-muted-foreground'>
                불러오는 중…
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell colSpan={7} className='text-center text-destructive'>
                품목을 불러오지 못했습니다.
              </TableCell>
            </TableRow>
          )}
          {data?.items.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={7} className='text-center text-muted-foreground'>
                조건에 맞는 품목이 없습니다.
              </TableCell>
            </TableRow>
          )}
          {data?.items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <ItemThumbnail path={item.imageUrl} alt={item.name} />
              </TableCell>
              <TableCell>{item.code}</TableCell>
              <TableCell>
                <Link
                  href={`/items/${item.id}`}
                  className='font-medium hover:underline'
                >
                  {item.name}
                </Link>
              </TableCell>
              <TableCell>
                <ItemCategoryBadge category={item.category} />
              </TableCell>
              <TableCell>{item.spec ?? "-"}</TableCell>
              <TableCell>{item.baseUnit}</TableCell>
              <TableCell>
                <Badge variant={item.isActive ? "default" : "outline"}>
                  {item.isActive ? "사용 중" : "사용 중지"}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data && (
        <div className='flex items-center justify-end gap-3 text-sm'>
          <span className='text-muted-foreground'>총 {data.meta.total}건</span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasPrev}
            onClick={() => setPage((p) => p - 1)}
          >
            이전
          </Button>
          <span>
            {data.meta.page} / {data.meta.totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
